import {
  BookingMethods,
  BookingStatusNames,
  bookingStatusType,
  BookingType,
  IBooking,
  IBookingStatus,
  IPaymentDetailsBase,
  IStatusCharacter,
  servicePaymentType,
  StatusCharacterType,
  StatusUnconfirmed,
  UnreliableBookingStatusIds,
} from 'shared-types/index';
import DiaryPaymentService from 'external-services/diary-payment-service';
import StatusConfigService from 'external-services/status-config-service';

const NS = 'StatusService';

// exported access to static functions (use StatusServiceInstance for functions that rely on angular dependencies)
export class StatusService {
  private getBookingStatus: () => {
    statusOptions: IBookingStatus[];
    fullStatusOptions: IBookingStatus[];
  };

  init(
    getBookingStatus: () => { statusOptions: IBookingStatus[]; fullStatusOptions: IBookingStatus[] }
  ): void {
    this.getBookingStatus = getBookingStatus;
  }

  /**
   * Updates the status object with the full payment pending status object
   * @returns the status name of the booking
   */
  updateBookingStatusWithPaymentPendingUnconfirmed(booking: IBooking): string {
    const pendingPaymentStatus = this.getBookingStatus().fullStatusOptions.find(
      ({ name, enabled }) => enabled && name === BookingStatusNames.PendingPayment
    );
    if (pendingPaymentStatus) {
      booking.status = pendingPaymentStatus;
    }
    return booking.status.name;
  }

  /**
   * Retrieves a list of status objects, filtered by Standby List and Payment Pending rules, with 'hide=false' values.
   * When booking.status is Payment Pending (which has 'hide=true' on it), this function will a Payment Pending status object to the list.
   * @param selectedStatusName - currently selected status name
   * @param booking - target booking object
   * @returns - list of filtered status objects
   */
  getFilteredStatusList(
    selectedStatusName: BookingStatusNames | '',
    booking: IBooking
  ): IBookingStatus[] {
    const newStatusOptions = this.getBookingStatus().statusOptions.filter((status) => {
      return StatusService.statusSelectFilter(
        selectedStatusName,
        status.name,
        booking.bookingType,
        booking.paymentDetails
      );
    });

    this.addCustomStatusOptions(newStatusOptions, booking.status);
    return newStatusOptions;
  }
  // new function for pass detail only, no need pass whole booking
  getFilteredStatusListByBookingDetails(
    selectedStatusName: BookingStatusNames | '',
    bookingType: BookingType,
    paymentDetails: IPaymentDetailsBase,
    status: IBookingStatus
  ): IBookingStatus[] {
    const newStatusOptions = this.getBookingStatus().statusOptions.filter((status) => {
      return StatusService.statusSelectFilter(
        selectedStatusName,
        status.name,
        bookingType,
        paymentDetails
      );
    });

    this.addCustomStatusOptions(newStatusOptions, status);
    return newStatusOptions;
  }
  /**
   * Returns a list of statuses used for new booking, replacing the old angularJS 'NewStatusList' filter.
   * New booking will never have a paymentPending or standbyPending status, since those only get added after saving, so the relevant
   * payment link can be created on back end with it. Therefore we don't need to call 'getFilteredStatusList' for new booking, like
   * we do in edit booking and we also don't need things like cancel, no show or finshed, since it doesn't make sense for a new booking.
   * The exception to this, however, is when move booking is used (which uses NewBooking behind the scenes).
   */
  getNewBookingFilteredStatusList(
    allowPP = false,
    allowSP = false,
    isMovedBooking = false
  ): IBookingStatus[] {
    const pendingPayment = this.getBookingStatus().fullStatusOptions.find(
      (status) => allowPP && status.name === BookingStatusNames.PendingPayment
    );

    const opts = pendingPayment ? [pendingPayment] : [];

    if (isMovedBooking) {
      return [
        ...this.getBookingStatus().statusOptions.filter((status) => {
          return (
            status.name !== BookingStatusNames.StandbyPending ||
            (allowSP && status.name === BookingStatusNames.StandbyPending)
          );
        }),
        ...opts,
      ];
    }

    return [
      ...this.getBookingStatus().statusOptions.filter((status) => {
        return (
          status.name === BookingStatusNames.Confirmed ||
          status.name === BookingStatusNames.Unconfirmed ||
          status.name === BookingStatusNames.Seated
        );
      }),
      ...opts,
    ];
  }

  /**
   * @returns BookingStatusNames - if Unconfirmed is enabled returns it's name, otherwise uses Confirmed name
   */
  getUnconfirmedStatusName(): BookingStatusNames {
    const unconfirmedStatus = this.getBookingStatus().statusOptions.find(
      ({ name }) => name === BookingStatusNames.Unconfirmed
    );
    return unconfirmedStatus ? BookingStatusNames.Unconfirmed : BookingStatusNames.Confirmed;
  }

  /**
   * @returns BookingStatusTypes - if Unconfirmed is enabled returns it's statusType, otherwise uses Confirmed statusType
   */
  getUnconfirmedStatusType(): bookingStatusType {
    const unconfirmedStatus = this.getBookingStatus().statusOptions.find(
      ({ name }) => name === BookingStatusNames.Unconfirmed
    );
    return unconfirmedStatus ? bookingStatusType.unconfirmed : bookingStatusType.confirmed;
  }

  /**
   * @returns IBookingStatus - gets a full copy of the PaymentPending object if it is enabled
   */
  getPaymentPendingStatus(): IBookingStatus {
    const pendingPaymentStatus = this.getBookingStatus().fullStatusOptions.find(
      ({ name, enabled }) => enabled && name === BookingStatusNames.PendingPayment
    );
    return pendingPaymentStatus || null;
  }

  // ported over from Diary editBooking.controller -> preUpdateSetStatus
  getNewStatusByName(
    newStatusName: string,
    oldStatusName: string,
    activeBookingPaymentType: servicePaymentType,
    orgBooking: IBooking
  ): {
    newStatus: IBookingStatus;
    hasChanged: boolean;
  } {
    let newStatus: IBookingStatus;
    if (!newStatusName) {
      newStatusName = this.getValidPaymentPendingStatus(activeBookingPaymentType, orgBooking);
    }

    if (newStatusName) {
      const fullStatusOptions = this.getBookingStatus().fullStatusOptions;
      const status = StatusConfigService.getStatusByName(newStatusName, fullStatusOptions);
      if (status) {
        newStatus = status;
      }
    } else {
      const statusOptions = this.getBookingStatus().statusOptions;
      const status = StatusConfigService.getStatusByName(newStatusName, statusOptions);
      if (status) {
        newStatus = status;
      }
    }

    return {
      newStatus,
      hasChanged: newStatus && orgBooking.status.name !== newStatus.name,
    };
  }

  private getValidPaymentPendingStatus(
    activeBookingPaymentType: servicePaymentType,
    orgBooking: IBooking
  ): BookingStatusNames {
    const orgPaymentDetails = DiaryPaymentService.getPaymentDetailsByBooking(orgBooking);
    const orgBookingIsFree =
      !orgPaymentDetails || orgPaymentDetails.paymentType === servicePaymentType.noPayment;
    const newBookingHasPayment = activeBookingPaymentType !== servicePaymentType.noPayment;
    if (orgBookingIsFree && newBookingHasPayment) {
      // they just added a payment set the status to payment pending
      return BookingStatusNames.PendingPayment;
    }
    return null;
  }

  /**
   * Adds the booking.status object to the
   * @param statusOptions - status options to add to 'statusOptions' if doesn't already exist
   * @param bookingStatus - status object from IBooking
   */
  private addCustomStatusOptions(
    statusOptions: IBookingStatus[],
    bookingStatus: IBookingStatus
  ): void {
    const statusNames = statusOptions.map((status) => status.name);
    const statusFound = statusNames.indexOf(bookingStatus?.name);
    if (statusFound === -1) {
      statusOptions.unshift(bookingStatus);
    }
  }

  static getStandbyPaymentPendingState(
    statusType: bookingStatusType,
    paymentDetails: IPaymentDetailsBase,
    bookingType: BookingType
  ): { isSBPaymentUnconfirmed: boolean; isSBPaymentPending: boolean } {
    const isPaymentPending = statusType === bookingStatusType.pendingPayment;
    const isStandbyPending = statusType === bookingStatusType.standbyPending;

    // is legit SB + payment
    const isStandbyHasPayment = StatusService.checkStandbyPendingPayment(
      paymentDetails,
      bookingType
    );

    // is still in Payment Pending status, but SB email has not yet been sent
    const isSBPaymentUnconfirmed = isStandbyHasPayment && isPaymentPending;

    // has payment and is in Standby Pending status, which means the SB email has been sent
    const isSBPaymentPending = isStandbyHasPayment && isStandbyPending;

    return { isSBPaymentUnconfirmed, isSBPaymentPending };
  }

  /**
   * Determines which statuses should be shown in the status selector
   * @param selectedStatusName - currently selected BookingStatusNames (use '' if none selected)
   * @param statusName - BookingStatusNames to check from list
   * @param venue - the venue object
   * @param bookingType - bookingType of the booking
   * @param paymentDetails - payment details of the booking
   * @returns
   */
  static statusSelectFilter(
    selectedStatusName: BookingStatusNames | '',
    statusName: BookingStatusNames | string,
    bookingType: BookingType,
    paymentDetails: IPaymentDetailsBase
  ): boolean {
    if (bookingType === BookingType.StandbyList) {
      if (
        statusName === BookingStatusNames.Seated ||
        statusName === BookingStatusNames.Finished
      ) {
        return false;
      }

      if (
        selectedStatusName === BookingStatusNames.StandbyPending &&
        statusName === BookingStatusNames.StandbyPending
      ) {
        return true;
      }

      const isStandbyHasPayment = this.checkStandbyPendingPayment(paymentDetails, bookingType);
      if (
        isStandbyHasPayment &&
        selectedStatusName === BookingStatusNames.PendingPayment &&
        statusName === BookingStatusNames.PendingPayment
      ) {
        return true;
      }
    }

    if (statusName === BookingStatusNames.StandbyPending) {
      return false;
    }

    return true;
  }

  /**
   * Checks if there is a payment on a standby booking
   * @param paymentDetails - payment details from booking object
   * @param bookingType - bookingType from booking object
   * @returns - true if payment exists and bookingType is StandbyList
   */
  static checkStandbyPendingPayment(
    paymentDetails: IPaymentDetailsBase,
    bookingType: BookingType
  ): boolean {
    if (bookingType === BookingType.StandbyList) {
      if (StatusService.hasPayment(paymentDetails)) {
        return true;
      }
    }
    return false;
  }

  static hasPayment(paymentDetails: IPaymentDetailsBase): boolean {
    return !!(
      paymentDetails &&
      paymentDetails.paymentType !== servicePaymentType.noPayment &&
      paymentDetails.price
    );
  }

  /**
   * Returns 1 or 2 characters for the booking status symbols along with info on how to display them
   */
  static getStatusCodeCharacters(
    status: IBookingStatus,
    paymentDetails: IPaymentDetailsBase,
    bookingType: BookingType
  ): { char1: IStatusCharacter; char2: IStatusCharacter } {
    const { isSBPaymentPending, isSBPaymentUnconfirmed } =
      StatusService.getStandbyPaymentPendingState(status.statusType, paymentDetails, bookingType);

    if (isSBPaymentUnconfirmed) {
      return {
        char1: { large: true, text: '$', type: StatusCharacterType.typeA },
        char2: { large: false, text: 'U' },
      };
    }

    if (isSBPaymentPending) {
      return {
        char1: { large: true, text: '$', type: StatusCharacterType.typeB },
        char2: { large: false, text: '' },
      };
    }

    if (
      bookingType !== BookingType.StandbyList &&
      status.statusType === bookingStatusType.pendingPayment
    ) {
      return {
        char1: { large: false, text: 'P' },
        char2: { large: true, text: '$', type: StatusCharacterType.typeB },
      };
    }

    // standard code
    return {
      char1: { large: false, text: status.code.charAt(0) },
      char2: { large: false, text: status.code.charAt(1) || '' },
    };
  }

  static populateMissingStatusInBooking(booking: IBooking): void {
    // bookings should always have a status, but just in case they are invalid, we treat them as unconfirmed
    console.warn(
      NS,
      'Detected booking without a status. Treating it as unconfirmed. This may be due to "dirty" data.',
      booking
    );
    booking.status = StatusUnconfirmed;
  }

  public forceSeatedWalkinToSeatedStatus(booking: IBooking): void {
    if (booking.method === BookingMethods.walkin && booking.tables?.length) {
      // the booking a walkin been seated change its status if this restaurant has seated status.
      const foundSeatedStatus = this.getBookingStatus().statusOptions.find(
        ({ name }) => BookingStatusNames.Seated === name
      );
      if (foundSeatedStatus) {
        booking.status = foundSeatedStatus;
      }
    }
  }
  static isBookingCancelled(booking: IBooking): boolean {
    if (!booking.status) {
      StatusService.populateMissingStatusInBooking(booking);
      return false;
    }
    return booking.status.statusType === 'cancelled';
  }
  static isBookingWaitList(booking: IBooking): boolean {
    return !booking.tables || booking.tables.length === 0;
  }

  /**
* Returns true if a status with a matching code and name already exist
* @param {*} status
* @returns true if a status with a matching code and name exist
*/
  static checkIfStatusExists(status: IBookingStatus, statuses: IBookingStatus[]) {
    if (!statuses) {
      return false;
    }
    if (!status) {
      return false;
    }
    const existingStatus = statuses.find(
      x => x.code.localeCompare(status.code, undefined, { sensitivity: 'accent' }) === 0
        && x.name.localeCompare(status.name, undefined, { sensitivity: 'accent' }) === 0
        && (!status._id || x._id !== status._id));  //If this is an edit, it's allowed

    if (existingStatus) {
      return true;
    } else {
      return false;
    }
  }
}

// creates a singleton instance to export (for functions that rely on angular dependencies)
const instance = new StatusService();
export { instance as StatusServiceInstance };
